import React, { useEffect, useRef, useState } from 'react';
import { Stage, Rect, Layer } from 'react-konva';
import type Konva from 'konva';

import { logError } from 'src/loggingManager';
import Loading from 'src/components/Loading';
import { downloadImage } from 'src/util/upload';
import { SPACE } from 'src/util/keycodes';
import { calculateCanvasScale } from 'src/util/misc';
import { addOnDownListener, addOnUpListener, removeOnDownListener, removeOnUpListener } from 'src/keyboardManager';
import { Provider, ReactReduxContext } from 'react-redux';
import { useLatest } from 'src/hooks/useLatest';
import { useDragGesture } from 'src/hooks/useDragGesture';
import Warps from './warps';
import GarmentMarkerOverlay from './garmentMarkers';
import Header from './header';
import Rulers from '../rulers';
import TraceRuler from '../traceRuler';
import transparentFillUrl from '../../../../../content/images/transparent.png';
import Cropping from '../SceneVariation/Cropping';
import type { CancelablePromise } from 'src/types/upload';
import type { KonvaEventObject } from 'konva/lib/Node';

interface Props {
    scale: number;
    offset: { x: number; y: number };
    image: { url?: string; width?: number; height?: number };
    errorRequestCount: number;
    successRequestCount: number;
    latestRequestCount: number;
    deselectLayer: () => void;
    moveCanvas: (offset: { x: number; y: number }) => void;
    zoomCanvas: (params: {
        scroll: number;
        image: any;
        container: HTMLElement;
        mouseX: number;
        mouseY: number;
    }) => void;
    centerCanvas: (params: { image: any; container: HTMLElement }) => void;
    isCroppingEnable: boolean;
    isTraceRulerEnabled: boolean;
    updateMousePosition: (position: { x: number; y: number } | null) => void;
}

const WHEEL_CONST = 120;

const Canvas: React.FC<Props> = ({
    scale,
    offset: offsetProp,
    image,
    errorRequestCount,
    successRequestCount,
    latestRequestCount,
    deselectLayer,
    moveCanvas,
    zoomCanvas,
    centerCanvas,
    isCroppingEnable,
    isTraceRulerEnabled,
    updateMousePosition,
}) => {
    const [transparentBackground, setTransparentBackground] = useState<HTMLImageElement | null>(null);
    const [spacePressed, setSpacePressed] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [offset, setOffset] = useState(offsetProp);

    // Mirrors `static getDerivedStateFromProps`: while not actively dragging, keep the local offset
    // in sync with the prop. Adjusting state during render (rather than in an effect) avoids an extra
    // stale-state render, matching the original's bail-out semantics.
    if (!dragging && offset !== offsetProp) {
        setOffset(offsetProp);
    }

    const containerRef = useRef<HTMLDivElement | null>(null);
    const stageRef = useRef<Konva.Stage | null>(null);
    const cancelableRequests = useRef<CancelablePromise<HTMLImageElement>[]>([]);

    // Drag bookkeeping that render() never reads directly — kept out of state so it doesn't trigger
    // re-renders on its own (only `moveCanvas`/the resulting `offset` prop update does that).
    const dragData = useRef<{ clientX: number; clientY: number; startX: number; startY: number }>({
        clientX: 0,
        clientY: 0,
        startX: 0,
        startY: 0,
    });

    // `onDragEnd` below needs to commit the *final* dragged `offset` to `moveCanvas` -- `useLatest`
    // guarantees that regardless of exactly when the gesture-ending handler fires relative to React's
    // own render cycle (see its doc comment).
    const offsetRef = useLatest(offset);

    useEffect(() => {
        const requests = cancelableRequests.current;
        const mountPromise = downloadImage(transparentFillUrl);
        requests.push(mountPromise);

        mountPromise
            .then((background) => {
                requests.splice(requests.indexOf(mountPromise), 1);
                setTransparentBackground(background);
            })
            .catch((errorResponse) => {
                logError(`Download of canvas image failed (url=${transparentFillUrl}): ${errorResponse}`);
            });

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.which === SPACE) {
                setSpacePressed(true);
            }
        };
        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.which === SPACE) {
                setSpacePressed(false);
            }
        };
        addOnDownListener(handleKeyDown);
        addOnUpListener(handleKeyUp);

        return () => {
            requests.forEach((p) => p.cancel?.());
            removeOnDownListener(handleKeyDown);
            removeOnUpListener(handleKeyUp);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Mirrors `componentDidUpdate`: only centers the canvas the moment `image.url` first becomes
    // truthy, never on mount and never again afterwards. `prevImageUrlRef` starts equal to the first
    // render's `image.url`, so this is a no-op the first time the effect runs (i.e. on mount).
    const prevImageUrlRef = useRef(image.url);
    useEffect(() => {
        const prevUrl = prevImageUrlRef.current;
        prevImageUrlRef.current = image.url;
        if (image.url && !prevUrl && containerRef.current) {
            centerCanvas({ image, container: containerRef.current });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [image.url]);

    // Attaches the non-passive wheel listener once the container exists, and re-attaches it whenever
    // the values it closes over change — equivalent to (and strictly safer than) the original's
    // every-render `addEventListener` call in its ref callback, which only avoided duplicate listeners
    // because `this.onWheel` was a stable bound method the browser deduped against.
    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return undefined;
        }
        const handleWheel = (e: WheelEvent) => {
            e.preventDefault();
            const scroll = -e.deltaY / WHEEL_CONST;
            const containerRect = container.getBoundingClientRect();
            const mouseX = 1 - (containerRect.width - (e.pageX - containerRect.left)) / containerRect.width;
            const mouseY = 1 - (containerRect.height - (e.pageY - containerRect.top)) / containerRect.height;
            zoomCanvas({ scroll, image, container, mouseX, mouseY });
        };
        container.addEventListener('wheel', handleWheel, { passive: false });
        return () => container.removeEventListener('wheel', handleWheel);
    }, [image, zoomCanvas]);

    const onDragMove = (e: MouseEvent) => {
        const container = containerRef.current;
        if (!container) {
            return;
        }
        const { clientX, clientY, startX, startY } = dragData.current;

        const containerRect = container.getBoundingClientRect();

        const finalScale = calculateCanvasScale(scale, container, image);

        let newX = startX - (clientX - e.clientX);
        let newY = startY - (clientY - e.clientY);
        const imageWidth = image.width! * finalScale;
        const imageHeight = image.height! * finalScale;
        const minX = -(imageWidth - containerRect.width * (2 / 3));
        const minY = -(imageHeight - containerRect.height * (2 / 3));
        const maxX = containerRect.width / 4;
        const maxY = containerRect.height / 4;
        newX = Math.min(Math.max(minX, newX), maxX);
        newY = Math.min(Math.max(minY, newY), maxY);

        offsetRef.current = { x: newX, y: newY };
        setOffset(offsetRef.current);
    };

    const { start: startPan } = useDragGesture({
        onDragMove,
        onDragEnd: () => {
            setDragging(false);
            moveCanvas(offsetRef.current);
        },
    });

    const onMouseDown = (e: KonvaEventObject<MouseEvent>) => {
        if (spacePressed) {
            dragData.current = {
                clientX: e.evt.clientX,
                clientY: e.evt.clientY,
                startX: offset.x,
                startY: offset.y,
            };
            setDragging(true);
            startPan();
            return;
        }
        deselectLayer();
    };

    const onMouseMove = (e: KonvaEventObject<MouseEvent>) => {
        if (isTraceRulerEnabled) {
            const stage = e.target.getStage();
            if (stage) {
                const pointerPosition = stage.getPointerPosition();
                if (pointerPosition) {
                    updateMousePosition(pointerPosition);
                }
            }
        }
    };

    const onMouseLeave = () => {
        if (isTraceRulerEnabled) {
            updateMousePosition(null);
        }
    };

    let canvas: JSX.Element | null = null;
    let backDrop: JSX.Element | null = null;
    if (image.width && image.height && transparentBackground && containerRef.current) {
        const container = containerRef.current;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        const finalScale = calculateCanvasScale(scale, container, image);
        const cursorChange = (cursor: string) => (container.style.cursor = cursor);

        backDrop = (
            <img
                data-testid='scene-maker-editor-background-image'
                className='scene-maker-editor__background'
                src={image.url}
                width={image.width * finalScale}
                height={image.height * finalScale}
                style={{ left: offset.x, top: offset.y }}
            />
        );

        // Redux workaround for React Konva: https://github.com/konvajs/react-konva/issues/311#issuecomment-536634446
        // This can be removed as soon as we upgrade React Konva to 18.2.2 or higher which requires React 18
        canvas = (
            <ReactReduxContext.Consumer>
                {({ store }) => (
                    <Stage
                        ref={stageRef}
                        style={spacePressed ? { cursor: '-webkit-grab' } : {}}
                        fontFamily='Open Sans, sans-serif'
                        className='scene-canvas'
                        width={containerWidth}
                        height={containerHeight}
                        onMouseDown={(e) => onMouseDown(e)}
                        onMouseMove={onMouseMove}
                        onMouseLeave={onMouseLeave}>
                        <Provider store={store}>
                            <Layer>
                                <Rect width={containerWidth} height={containerHeight} />
                            </Layer>
                            <Warps
                                container={container}
                                cursorChange={cursorChange}
                                scale={finalScale}
                                offset={offset}
                            />
                            <GarmentMarkerOverlay
                                cursorChange={cursorChange}
                                scale={finalScale}
                                offset={offset}
                                canvasWidth={containerWidth}
                            />
                            <Rulers
                                container={container}
                                canvasWidth={containerWidth}
                                canvasHeight={containerHeight}
                                cursorChange={cursorChange}
                                scale={finalScale}
                                offset={offset}
                            />
                            <TraceRuler canvasWidth={containerWidth} canvasHeight={containerHeight} />
                            {isCroppingEnable && (
                                <Cropping
                                    canvasWidth={containerWidth}
                                    canvasHeight={containerHeight}
                                    offset={offset}
                                    scale={finalScale}
                                    // Narrowed by this block's own `image.width && image.height` check above,
                                    // which TS can't see through the `image` object reference itself.
                                    image={image as { width: number; height: number }}
                                />
                            )}
                        </Provider>
                    </Stage>
                )}
            </ReactReduxContext.Consumer>
        );
        // The Rect in the above JSX is necessary for dragging the canvas around with the spacebar.
    }
    return (
        <div className='scene-maker-editor'>
            <Header image={image} container={containerRef.current} />
            <div ref={containerRef} className='scene-maker-editor__canvas'>
                {backDrop}
                {canvas}
                <Loading show={errorRequestCount !== latestRequestCount && latestRequestCount > successRequestCount} />
            </div>
        </div>
    );
};

export default Canvas;
